// The Kulki board (.ai/game-rules.md "Kulki"): the game of kulki/rules.ts, the player's pick and move, the move shown
// step by step (the ball runs its path, a line leaves, the new balls grow in, their lines leave), one step of Undo (owner
// 2026-11-03), the best score in this browser, and the clock. The clock stops while the Pause window is open or the tab
// is hidden.
import { useSyncExternalStore } from 'react';
import { boardText, LINE, move, newGame as startGame, reachable, type Cell, type KulkiState } from './rules';
import { SLOW } from '../hearts/store';

/** A ball's step from one cell to the next on its path. */
export const STEP_MS = 38;
/** Balls of a line shrink away, new balls grow in (kulki.css uses the same times). */
export const LEAVE_MS = 300;
export const GROW_MS = 240;
const BEST_KEY = 'solitaire_storage:kulki:best';

export interface BoardState {
  readonly game: KulkiState | null;
  /** The cells shown now: the game's own, or a step of a move being shown. */
  readonly cells: readonly Cell[];
  /** The ball picked to move (a cell), or null. */
  readonly selected: number | null;
  /** The ball running its path: its colour and cell. */
  readonly moving: { readonly colour: number; readonly at: number } | null;
  /** Cells whose balls are leaving (a line) or growing in (new balls). */
  readonly leaving: readonly number[];
  readonly arriving: readonly number[];
  /** A cell the picked ball cannot reach, shown for a moment (a counter restarts it). */
  readonly blocked: { readonly at: number; readonly n: number } | null;
  /** The points of the last line, over its middle cell (a counter restarts it). */
  readonly gain: { readonly points: number; readonly at: number; readonly n: number } | null;
  /** A move is being shown: taps wait. */
  readonly busy: boolean;
  /** The game before the last move (one step of Undo), null when there is none. */
  readonly prev: KulkiState | null;
  readonly seconds: number;
  /** The best score in this browser (before this game ended). */
  readonly best: number;
  /** A position put in by the e2e hook (load): its end is never saved. */
  readonly demo: boolean;
  /** When the game began and ended (Date.now()). */
  readonly startedAt: number;
  readonly endedAt: number | null;
}

export interface KulkiStore {
  getState(): BoardState;
  subscribe(fn: () => void): () => void;
  /** A new game: `pattern` (the deal's numbers, engine/shuffle.ts) seeds it (Retry, a report); `line` the version. */
  newGame(pattern: readonly number[], line?: number): void;
  /** A tap on a cell: pick a ball, pick another, or move the picked one there. */
  tap(at: number): void;
  undo(): void;
  stop(): void;
  /** e2e only: show a position. */
  load(g: KulkiState): void;
}

export interface KulkiHooks {
  paused: () => boolean;
  onStep?: (what: string) => void;
  /** The board is full: the game is over. */
  onEnd?: (s: KulkiState) => void;
}

/** The best score of a version: 5 in a row under the plain key, 4 in a row under its own. */
const bestKey = (line: number) => (line === LINE ? BEST_KEY : `${BEST_KEY}:${line}`);
function readBest(line: number): number {
  try {
    return Number(localStorage.getItem(bestKey(line))) || 0;
  } catch {
    return 0;
  }
}
function saveBest(line: number, n: number): void {
  try {
    localStorage.setItem(bestKey(line), String(n));
  } catch {
    /* no storage */
  }
}

export function createKulkiStore(hooks: KulkiHooks): KulkiStore {
  const empty: Cell[] = [];
  let state: BoardState = {
    game: null, cells: empty, selected: null, moving: null, leaving: [], arriving: [], blocked: null, gain: null, busy: false,
    prev: null, seconds: 0, best: readBest(LINE), demo: false, startedAt: 0, endedAt: null,
  };
  const listeners = new Set<() => void>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let clock: ReturnType<typeof setInterval> | null = null;
  let counter = 0;
  const set = (patch: Partial<BoardState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const step = (what: string) => hooks.onStep?.(what);
  const after = (ms: number, fn: () => void) => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms * SLOW);
    timers.add(id);
  };
  const clearTimers = () => {
    for (const id of timers) clearTimeout(id);
    timers.clear();
  };
  const startClock = () => {
    if (clock !== null) return;
    clock = setInterval(() => {
      const g = state.game;
      if (!g || g.over || hooks.paused()) return;
      set({ seconds: state.seconds + 1 });
    }, 1000);
  };
  const stopClock = () => {
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  /** The game is in place, nothing moves. */
  const settle = (g: KulkiState) => {
    set({ game: g, cells: g.cells, moving: null, leaving: [], arriving: [], busy: false });
    if (!g.over) return;
    stopClock();
    const best = state.best;
    if (!state.demo && g.score > best) saveBest(g.line, g.score);
    set({ endedAt: Date.now(), prev: null });
    step(`kulki over: score ${g.score}, ${g.moves} moves, best before ${best}`);
    hooks.onEnd?.(g);
  };

  /** The middle cell of a line, for the points shown over it. */
  const middle = (cells: readonly number[]) => cells[Math.floor(cells.length / 2)]!;

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame(pattern, line = LINE) {
      clearTimers();
      const g = startGame(pattern, line);
      set({ demo: false, prev: null, selected: null, blocked: null, gain: null, seconds: 0, best: readBest(line), startedAt: Date.now(), endedAt: null });
      settle(g);
      set({ arriving: g.cells.flatMap((v, i) => (v ? [i] : [])) });
      after(GROW_MS, () => set({ arriving: [] }));
      startClock();
      step(`kulki new game, ${line} in a row`);
    },
    tap(at) {
      const g = state.game;
      if (!g || g.over || state.busy) return;
      if (g.cells[at]) {
        set({ selected: state.selected === at ? null : at });
        return;
      }
      const from = state.selected;
      if (from === null) return;
      const r = move(g, from, at);
      if (!r) {
        set({ blocked: { at, n: ++counter } });
        return;
      }
      step(`kulki move ${from} -> ${at}${r.points ? `, +${r.points}` : ''}`);
      const colour = g.cells[from]!;
      const before = [...g.cells];
      before[from] = 0;
      set({ prev: g, selected: null, busy: true, cells: before, moving: { colour, at: from } });
      // the run: one cell a step
      r.path.slice(1).forEach((cell, k) => after(STEP_MS * (k + 1), () => set({ moving: { colour, at: cell } })));
      const ran = STEP_MS * (r.path.length - 1);
      const landed = [...before];
      landed[at] = colour;
      after(ran, () => {
        set({ moving: null, cells: landed });
        if (r.cleared.length) {
          set({ leaving: r.cleared, gain: { points: r.points, at: middle(r.cleared), n: ++counter } });
          after(LEAVE_MS, () => settle(r.state));
          return;
        }
        // the new balls grow in; then their own lines leave
        const grown = [...landed];
        r.added.forEach((i, k) => (grown[i] = g.next[k]!)); // in the order of the next balls (rules.ts place)
        set({ cells: grown, arriving: r.added });
        after(GROW_MS, () => {
          set({ arriving: [] });
          if (!r.clearedAfter.length) return settle(r.state);
          set({ leaving: r.clearedAfter, gain: { points: r.points, at: middle(r.clearedAfter), n: ++counter } });
          after(LEAVE_MS, () => settle(r.state));
        });
      });
    },
    undo() {
      const p = state.prev;
      if (!p || state.busy || state.game?.over) return;
      step('kulki undo');
      set({ prev: null, selected: null, blocked: null, gain: null });
      settle(p);
    },
    stop() {
      clearTimers();
      stopClock();
      // a move being shown jumps to its end, silently: the end of the game is not announced again (a race that ended
      // during the last move's animation must not open the solo end window)
      const g = state.game;
      if (state.busy && g) set({ cells: g.cells, moving: null, leaving: [], arriving: [], busy: false });
    },
    load(g) {
      clearTimers();
      set({ demo: true, prev: null, selected: null, blocked: null, gain: null, best: readBest(g.line ?? LINE), startedAt: Date.now(), endedAt: null });
      settle(g);
      startClock();
    },
  };
}

/** Every cell the picked ball can reach (the view marks them faintly). */
export const reachableFrom = (s: BoardState): Set<number> =>
  s.game && s.selected !== null && !s.busy ? reachable(s.game.cells, s.selected) : new Set();

let current: KulkiStore | null = null;
export const setKulkiStore = (s: KulkiStore) => {
  current = s;
};
export const kulkiStore = (): KulkiStore => {
  if (!current) throw new Error('kulkiStore: not created');
  return current;
};
export function useKulki(): BoardState {
  const s = kulkiStore();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}

/** The board as text for a report. */
export function kulkiBoardText(st: BoardState): Record<string, unknown> | null {
  const g = st.game;
  if (!g) return null;
  return { board: boardText(g), selected: st.selected, busy: st.busy, undo: !!st.prev, pattern: g.pattern.join(',') };
}
